import { Input } from '@cokitchen/cokitchen-components';
import CurrencyInput from 'components/base/currency-input';
import SelectDropdown from 'components/base/select-dropdown';
import useDashboardContext from 'contexts/dashboard.context';
import {
  TransferRequestItemType,
  TransferRequestType,
  ENUM_TRANSFER_STATUS
} from 'models/transfer-request.model';
import { BatchType } from 'models/item-request.model';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { getTotalQuantitySent, reduceBatches } from 'utils/functions';
import { removeBatch, updateBatchNo, updateBatchQuantity } from './update-transfer-request/utils';
import { getTransferStoreTypeFromPathname, normalizeTransferStoreType } from './store-type';

type BatchDropdownOption = {
  value: string;
  name: string;
  id: string;
  price: string;
  batchNo: number;
  quantity: number;
};

const getBatchNoFromName = (name: string) => Number(String(name).replace(/[^0-9]/g, ''));

type Props = {
  data: TransferRequestItemType;
  transferToUpdate: TransferRequestType;
  setTransferToUpdate: React.Dispatch<React.SetStateAction<TransferRequestType>>;
};

const TransferRequestItemDetails = ({ data, transferToUpdate, setTransferToUpdate }: Props) => {
  const [batches, setBatches] = useState<BatchDropdownOption[]>([]);
  const { activeStore } = useDashboardContext();
  const location = useLocation();
  const storeType = normalizeTransferStoreType(getTransferStoreTypeFromPathname(location.pathname));
  const [loadingBatches, setLoadingBatches] = useState(false);
  const isRequested = transferToUpdate.status === ENUM_TRANSFER_STATUS.REQUESTED;

  const getIngredientBatches = async () => {
    if (!activeStore?.id) return;
    setLoadingBatches(true);
    try {
      const res: { data: { data: any } } = await apiService.get(
        `ingredients/${data.ingredient_id}/stores/${activeStore.id}`,
        { version: 'v2' }
      );
      const activeBatchKey = storeType === 'KITCHEN_STORE' ? 'kitchen_batches' : 'main_batches';
      setBatches(
        (res.data.data?.[activeBatchKey] ?? []).map((batch: any) => ({
          value: batch.id,
          name: `Batch ${String(batch.batch_no)}`,
          id: batch.id,
          price: `${batch.quantity.toString()} units`,
          batchNo: Number(batch.batch_no),
          quantity: Number(batch.quantity) || 0
        }))
      );
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    getIngredientBatches();
  }, [activeStore?.id, data.ingredient_id, storeType]);

  const availableBatches = useMemo(() => {
    const selectedBatchIds = new Set(
      (data.batches as BatchType[]).map((batch) => batch.batch_id).filter(Boolean)
    );
    return batches.filter((batch) => !selectedBatchIds.has(batch.id));
  }, [batches, data.batches]);

  const addNewBatch = () => {
    if (availableBatches.length === 0) return;

    setTransferToUpdate((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === data.id
          ? {
              ...item,
              batches: [...data.batches, { batch_no: 0, quantity: 0, batch_id: '' }]
            }
          : item
      )
    }));
  };

  const updateQuantityReceived = (value: string) => {
    setTransferToUpdate((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === data.id ? { ...item, quantity_received: value } : item
      )
    }));
  };

  return (
    <div className='w-full py-[24px] px-[28px] border-b border-b-[#F7F7F8]' key={data.id}>
      <div className='w-full grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6'>
        <Input
          design='bordered'
          label='Item Name'
          name='name'
          placeholder='Item Name'
          value={data?.ingredient.name}
          disabled
        />
        <CurrencyInput
          label='Quantity requested'
          placeholder='Quantity requested'
          name='quantity_requested'
          value={data.quantity}
          hideCurrency
          explicitRender
          onWheel={(e) => e.currentTarget.blur()}
          onChange={() => {
            return;
          }}
        />
        <CurrencyInput
          label='Total quantity sent'
          placeholder='Quantity Sent'
          name='quantity_sent'
          hideCurrency
          explicitRender
          onWheel={(e) => e.currentTarget.blur()}
          value={getTotalQuantitySent(data.batches)}
          error={
            getTotalQuantitySent(data.batches) > data.quantity
              ? 'Total quantity sent cannot exceed requested quantity.'
              : ''
          }
          readOnly
          onChange={() => {
            return;
          }}
        />
        {activeStore?.id !== transferToUpdate.from_store_id && (
          <CurrencyInput
            label='Total quantity received'
            placeholder='Quantity received'
            name='quantity_received'
            hideCurrency
            explicitRender
            onWheel={(e) => e.currentTarget.blur()}
            value={data.quantity_received}
            error={
              Number(data.quantity_received || 0) > getTotalQuantitySent(data.batches)
                ? 'Total quantity received cannot exceed quantity sent.'
                : ''
            }
            onChange={updateQuantityReceived}
          />
        )}
      </div>

      <div>
        {reduceBatches(data.batches)
          .sort((a, b) => a.batch_no - b.batch_no)
          .map((batch, index) => {
            const selectedBatch =
              batches.find((option) => option.id === batch.batch_id) ??
              batches.find((option) => option.batchNo === batch.batch_no);
            const maximumQuantity = selectedBatch?.quantity ?? 0;
            const batchOptions = selectedBatch
              ? [
                  selectedBatch,
                  ...availableBatches.filter((option) => option.id !== selectedBatch.id)
                ]
              : availableBatches;

            return (
              <div key={index}>
                <div className='flex justify-between gap-8 py-3'>
                  <div className='w-full'>
                    <SelectDropdown
                      label='Batch'
                      placeholder='Batch 3'
                      value={selectedBatch?.name ?? `Batch ${batch.batch_no || ''}`}
                      setValue={(val) =>
                        updateBatchNo(
                          { ...val, batchNo: val.batchNo ?? getBatchNoFromName(val.name) },
                          batch,
                          setTransferToUpdate,
                          data
                        )
                      }
                      options={batchOptions}
                      disabled={!isRequested}
                      loading_options={loadingBatches}
                    />
                  </div>

                  <div className='w-full'>
                    <Input
                      design='bordered'
                      label='Quantity sent'
                      placeholder='0'
                      name='quantity_sent'
                      type='number'
                      value={batch.quantity}
                      disabled={!isRequested}
                      onChange={(e) => {
                        if (Number(e.target.value) <= maximumQuantity) {
                          updateBatchQuantity(batch, e, setTransferToUpdate, data);
                        }
                      }}
                      subAction={
                        isRequested && (
                          <div
                            onClick={() => {
                              updateBatchQuantity(
                                batch,
                                { target: { name: 'quantity_sent', value: maximumQuantity } },
                                setTransferToUpdate,
                                data
                              );
                            }}
                            className='py-1 px-1 rounded-sm'>
                            <p className='text-sm text-blue-5'>Max Quantity</p>
                          </div>
                        )
                      }
                    />
                  </div>
                </div>

                {isRequested && (
                  <div className='flex justify-end mt-3'>
                    <button
                      onClick={() => removeBatch(batch, setTransferToUpdate, data)}
                      className='text-red-5'>
                      - Remove batch
                    </button>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {isRequested && (
        <div>
          {transferToUpdate.from_store_id === activeStore?.id ? (
            <div />
          ) : availableBatches.length === 0 ? (
            <p className='text-gray-3'>There is no available batch in the store</p>
          ) : (
            <button onClick={addNewBatch} className='text-[#0080E5]'>
              + Add new batch
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TransferRequestItemDetails;
